// Zoom and pan for big grids as pure functions on SVG view rectangles. The view always keeps
// the frame's aspect ratio, stays inside the frame and shows between the whole frame (scale 1)
// and 1/maxScale of it.

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** A point as fractions (0–1) of the displayed area, from its left and top. */
export interface Fraction {
  fx: number;
  fy: number;
}

export function scaleOf(frame: Rect, view: Rect): number {
  return frame.w / view.w;
}

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

/** A view of `scale` whose point at `at` (fractions of the view) is the SVG point (px, py). */
function placed(frame: Rect, scale: number, px: number, py: number, at: Fraction): Rect {
  const w = frame.w / scale;
  const h = frame.h / scale;
  return {
    x: clamp(px - at.fx * w, frame.x, frame.x + frame.w - w),
    y: clamp(py - at.fy * h, frame.y, frame.y + frame.h - h),
    w,
    h,
  };
}

/** Zooms by `factor` (above 1 zooms in) keeping the point under `at` where it is. */
export function zoomAt(
  frame: Rect,
  view: Rect,
  factor: number,
  at: Fraction,
  maxScale: number,
): Rect {
  const scale = clamp(scaleOf(frame, view) * factor, 1, maxScale);
  return placed(frame, scale, view.x + at.fx * view.w, view.y + at.fy * view.h, at);
}

/**
 * A pinch: the SVG point that was under the fingers' midpoint at the start (`from`, in the
 * `start` view) follows the midpoint to `to`, and the scale follows the fingers' spread.
 */
export function pinch(
  frame: Rect,
  start: Rect,
  from: Fraction,
  to: Fraction,
  spread: number,
  maxScale: number,
): Rect {
  const scale = clamp(scaleOf(frame, start) * spread, 1, maxScale);
  return placed(frame, scale, start.x + from.fx * start.w, start.y + from.fy * start.h, to);
}

/** Moves the view by (dx, dy) SVG units. */
export function panBy(frame: Rect, view: Rect, dx: number, dy: number): Rect {
  return placed(frame, scaleOf(frame, view), view.x + dx, view.y + dy, { fx: 0, fy: 0 });
}

/** Centres the view on the SVG point (px, py), at `scale`. */
export function centreOn(frame: Rect, scale: number, px: number, py: number): Rect {
  return placed(frame, scale, px, py, { fx: 0.5, fy: 0.5 });
}

/** The smallest pan that brings `target` (SVG units) fully into the view, with a margin. */
export function ensureVisible(frame: Rect, view: Rect, target: Rect, margin = 0): Rect {
  let { x, y } = view;
  if (target.x - margin < x) x = target.x - margin;
  else if (target.x + target.w + margin > x + view.w) x = target.x + target.w + margin - view.w;
  if (target.y - margin < y) y = target.y - margin;
  else if (target.y + target.h + margin > y + view.h) y = target.y + target.h + margin - view.h;
  if (x === view.x && y === view.y) return view;
  return panBy(frame, view, x - view.x, y - view.y);
}
